import React from 'react'
import { X } from 'lucide-react'
import { cn } from './cn'

export interface ChipProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** 提供時顯示 × 移除鈕。× = 解除關聯／從集合移除（即時、不確認），對齊 brand-spec icon 語意 */
  onRemove?: () => void
  /** × 鈕的 aria-label（無障礙；onRemove 時應提供） */
  removeLabel?: string
}

/**
 * 膠囊（.chip）：詞條／提示 chip，＋選用 × 即時移除。高度、字級、間距與 × 的命中區讀
 * `--*-chip` 角色 token：:root 是網頁的長相，App 面（桌面詞典、引導）換成設計稿的
 * 34 高、15px、28px 命中區（kiao-tokens.css）。
 */
export function Chip({ children, onRemove, removeLabel, className, ...rest }: ChipProps) {
  return (
    <span
      className={cn(
        'inline-flex min-h-[var(--h-chip)] items-center gap-[var(--gap-chip)] rounded-full border border-border bg-background py-1 pl-3 pr-[var(--pad-chip-end)] text-[length:var(--fs-chip)] leading-[var(--leading-chip)] text-foreground',
        className
      )}
      {...rest}
    >
      {children}
      {onRemove ? (
        <button
          type="button"
          aria-label={removeLabel}
          onClick={onRemove}
          className="grid size-[var(--size-chip-x)] shrink-0 place-items-center rounded-full text-muted hover:bg-[var(--chip-x-hover-bg)] hover:text-[var(--chip-x-hover)] focus-visible:outline-2 focus-visible:outline-[var(--focus-ring)]"
        >
          <X aria-hidden="true" className="size-[var(--size-chip-x-icon)]" />
        </button>
      ) : null}
    </span>
  )
}

export type ChipsProps = React.HTMLAttributes<HTMLDivElement>

/** chip 群組容器（.chips）。 */
export function Chips({ className, ...rest }: ChipsProps) {
  return <div className={cn('flex flex-wrap gap-[var(--gap-xs)]', className)} {...rest} />
}

export interface TagProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** .tag-fill：實心墨底白字 */
  fill?: boolean
  /**
   * 跟在標題文字後面的描邊小標（App 面 `.tag`，例如設定檔清單的「預設」）：11px Sans、
   * 不轉大寫、圓角 4px，左邊自帶一點距離。不跟 `fill` 一起用。
   */
  inline?: boolean
}

/** 唯讀標籤（.tag / .tag-fill）：mono 小字 pill，非互動（與可移除的 .chip 區分）。 */
export function Tag({ fill, inline = false, className, ...rest }: TagProps) {
  if (inline) {
    return (
      <span
        className={cn(
          'ml-1.5 inline-flex items-center rounded-[var(--radius-tag)] border border-border px-1.25 py-px align-middle text-2xs font-normal leading-normal text-muted',
          className
        )}
        {...rest}
      />
    )
  }
  return (
    <span
      className={cn(
        'inline-flex min-h-[26px] items-center rounded-full border border-border px-2.5 pb-0.5 pt-1 font-mono text-2xs leading-none uppercase tracking-[0.08em] text-muted',
        fill && 'border-foreground bg-foreground text-background',
        className
      )}
      {...rest}
    />
  )
}
